import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig
} from 'axios'
import { showToast } from 'vant'
import router from '@/router'
import { useUserStore } from '@/store'
import { showLoginGuide } from '@/composables/useLoginGuide'

/**
 * 通用后端响应体（与后端 Result<T> 对齐）
 */
export interface ApiResult<T = any> {
  code: number
  message: string
  data: T
  timestamp: number
}

/**
 * 扩展 AxiosRequestConfig，增加 _retry / _loginGuideShown / _silent 标记
 *  - _retry：401 刷新 token 后重试，避免死循环
 *  - _loginGuideShown：40101 弹窗后重试，避免无限弹窗
 *  - _silent：静默模式，错误时不弹 toast（用于列表空态兜底等场景）
 */
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
  _loginGuideShown?: boolean
  _silent?: boolean
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL

const service: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
})

/**
 * 深层递归 sanitize：null / undefined → '-'，数字 NaN / Infinity → '-'
 * 保持对象 / 数组结构
 */
export function sanitizeData<T = any>(obj: T): T {
  if (obj === null || obj === undefined) {
    return '-' as unknown as T
  }
  if (typeof obj === 'number') {
    if (Number.isNaN(obj) || !Number.isFinite(obj)) {
      return '-' as unknown as T
    }
    return obj
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeData(item)) as unknown as T
  }
  if (typeof obj === 'object') {
    const result: Record<string, any> = {}
    for (const key of Object.keys(obj as Record<string, any>)) {
      result[key] = sanitizeData((obj as Record<string, any>)[key])
    }
    return result as T
  }
  return obj
}

// 请求拦截器：注入 Bearer Token（tenant_id 只从 JWT 解析，严禁前端传参）
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const userStore = useUserStore()
    if (userStore.accessToken) {
      config.headers.Authorization = `Bearer ${userStore.accessToken}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// 响应拦截器：统一业务码处理、401 自动刷新 token、脱敏友好提示
service.interceptors.response.use(
  async (response: AxiosResponse<ApiResult>) => {
    const res = response.data
    const config = response.config as RetryableRequestConfig

    if (res.code === 200) {
      // code === 200 时对 data 做一次 sanitize
      res.data = sanitizeData(res.data)
      return res as any
    }

    // ============ 游客模式：40101 弹登录引导弹窗 ============
    // 与 401 区分：
    //   401   = token 过期/无效（已登录用户，触发 refresh token 流程）
    //   40101 = 游客未登录写操作（触发登录引导弹窗，不 refresh）
    if (res.code === 40101) {
      // 防止弹窗后重试又返回 40101 的无限循环
      if (config._loginGuideShown) {
        return Promise.reject(res)
      }
      config._loginGuideShown = true

      // 显示登录弹窗；登录成功 → 自动重试原请求；用户取消 → reject
      return showLoginGuide()
        .then(async () => {
          // 登录成功，userStore 已更新 token；请求拦截器会注入新 token
          // 重试原请求，返回结果给原 caller（caller 感知不到 40101 中断）
          const retryRes = await service.request(config)
          return retryRes
        })
        .catch(() => {
          // 用户取消登录：原 caller 收到 40101 rejection
          return Promise.reject(res)
        })
    }

    // 业务失败：401 尝试刷新 token
    if (res.code === 401) {
      const userStore = useUserStore()
      const refreshToken = userStore.refreshToken

      // 没有 refreshToken，或已经重试过一次 → 直接登出
      if (!refreshToken || config._retry) {
        userStore.logout()
        showToast({ type: 'fail', message: '登录已过期，请重新登录' })
        router.replace({ path: '/login' }).catch(() => {})
        return Promise.reject(res)
      }

      // 标记即将重试，避免死循环
      config._retry = true

      try {
        // 用独立的 axios instance 调 /auth/refresh，不走当前拦截器（避免循环）
        const refreshInstance: AxiosInstance = axios.create({
          baseURL: BASE_URL,
          timeout: 15000,
          headers: { 'Content-Type': 'application/json;charset=UTF-8' }
        })
        const refreshResp = await refreshInstance.post<
          ApiResult<{ accessToken: string; refreshToken: string }>
        >('/auth/refresh', { refreshToken })
        const refreshRes = refreshResp.data

        if (refreshRes.code === 200 && refreshRes.data) {
          const { accessToken: newAccess, refreshToken: newRefresh } = refreshRes.data
          userStore.setTokens(newAccess, newRefresh)

          // 移除旧 Authorization，写入新 token，重发 1 次原请求
          delete config.headers.Authorization
          config.headers.Authorization = `Bearer ${newAccess}`
          return service.request(config) as any
        } else {
          // 刷新失败 → 登出
          userStore.logout()
          showToast({ type: 'fail', message: '登录已过期，请重新登录' })
          router.replace({ path: '/login' }).catch(() => {})
          return Promise.reject(res)
        }
      } catch (_e) {
        // 刷新异常 → 登出
        userStore.logout()
        showToast({ type: 'fail', message: '登录已过期，请重新登录' })
        router.replace({ path: '/login' }).catch(() => {})
        return Promise.reject(res)
      }
    }

    // 其他业务失败：友好提示
    if (!config._silent) {
      showToast({ type: 'fail', message: res.message || '请求失败，请稍后重试' })
    }
    return Promise.reject(res)
  },
  (error) => {
    // HTTP 层错误：脱敏
    const config = error?.config as RetryableRequestConfig | undefined
    if (!config?._silent) {
      let msg = '网络异常，请稍后重试'
      if (error?.code === 'ECONNABORTED') msg = '请求超时，请稍后重试'
      showToast({ type: 'fail', message: msg })
    }
    return Promise.reject(error)
  }
)

/**
 * 通用 GET/POST 封装
 */
export const request = {
  get<T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
    return service.get(url, { params, ...config }) as any
  },
  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
    return service.post(url, data, config) as any
  },
  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
    return service.put(url, data, config) as any
  },
  delete<T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
    return service.delete(url, { params, ...config }) as any
  },
  /** 静默 GET：错误时不弹 toast（用于列表空态兜底等场景） */
  getSilent<T = any>(url: string, params?: any): Promise<ApiResult<T>> {
    return service.get(url, { params, _silent: true } as any) as any
  },
  /** 静默 POST：错误时不弹 toast（用于点赞等轻量交互） */
  postSilent<T = any>(url: string, data?: any): Promise<ApiResult<T>> {
    return service.post(url, data, { _silent: true } as any) as any
  }
}

export default service
